import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSidebarMatterRecordOpened } from '@/analytics/utils/matter-record-analytics'
import { trackSidebarLinkClicked } from '@/analytics/utils/sidebar-analytics'
import { Plus } from '@/assets/icons'
import { Tag } from '@/components/tag/Tag'
import { BuildMatterRecordModal } from '@/matter-asset-common/selection/BuildMatterRecordModal'
import { useBuildMatterRecordModal } from '@/routes/dashboard/sidebar/hooks/useBuildMatterRecordModal'
import { MatterRecordsItem } from '../side-nav-item/MatterRecordsItem'
import { useMatterRecordsItems } from './hooks/useMatterRecordsItems'

type MatterRecordsProps = {
  matterId: string
  activeItemId?: string
  onNavigate?: () => void
}

/**
 * Self-contained Matter Records sidebar section.
 *
 * Owns asset selection, permission checks, item construction, and the Build a new record modal.
 * The parent (`SideNavMatter`) only decides whether to render this section at all.
 */
export function MatterRecords({ matterId, activeItemId, onNavigate }: MatterRecordsProps) {
  const { trackEvent } = useAnalytics()
  const { items, showBuildButton } = useMatterRecordsItems(matterId)
  const { open, onBuildRecord, onOpenChange, onContinue, onRecordTypeSelected, onViewExistingRecord, existingRecordTypes } =
    useBuildMatterRecordModal(onNavigate)

  return (
    <>
      <div className="flex flex-col gap-1 px-2">
        <div className="flex h-7 shrink-0 items-center px-2">
          <span className="font-rethink-sans text-xs font-medium uppercase text-text-tertiary">Matter Records</span>
        </div>

        {items.map((item) => (
          <MatterRecordsItem
            key={item.id}
            to={item.href}
            icon={item.icon}
            label={item.label}
            isActive={activeItemId === item.id}
            onClick={() => {
              trackSidebarMatterRecordOpened(trackEvent, matterId, item.id)
              trackSidebarLinkClicked(trackEvent, item.id, false)
              onNavigate?.()
            }}
            trailingAction={
              item.showBetaTag ? (
                <Tag variant="filled" color="Black" size="2xs">
                  beta
                </Tag>
              ) : undefined
            }
          />
        ))}

        {showBuildButton && <MatterRecordsItem icon={Plus} label="Build a new record" onClick={onBuildRecord} />}
      </div>

      <BuildMatterRecordModal
        open={open}
        onOpenChange={onOpenChange}
        onContinue={onContinue}
        onRecordTypeSelected={onRecordTypeSelected}
        existingRecordTypes={existingRecordTypes}
        onViewExistingRecord={onViewExistingRecord}
      />
    </>
  )
}
